import React, { useMemo } from "react";

import { Box, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGrid, GridColDef, GridRowsProp, GridSortModel, GridCallbackDetails } from "@mui/x-data-grid";

interface UniversalDataGridProps {
  rows: GridRowsProp;
  columns: GridColDef[];
  title?: string;
  loading?: boolean;
  height?: number | string;
  hideFooter?: boolean;
  disablePagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  // Server-side props
  rowCount?: number;
  paginationMode?: "client" | "server";
  sortingMode?: "client" | "server";
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  onSortModelChange?: (model: GridSortModel, details: GridCallbackDetails) => void;
  page?: number;
}

export const UniversalDataGrid: React.FC<UniversalDataGridProps> = React.memo(
  ({
    rows,
    columns,
    title,
    loading = false,
    height = 600,
    hideFooter = false,
    pageSize = 25,
    pageSizeOptions = [10, 25, 50, 100],
    // Server-side props
    rowCount,
    paginationMode = "client",
    sortingMode = "client",
    onPaginationModelChange,
    onSortModelChange,
    page = 0,
  }) => {
    const theme = useTheme();

    // Memoize stable props to prevent unnecessary re-renders
    const stableProps = useMemo(
      () => ({
        initialState: {
          pagination: {
            paginationModel: { pageSize, page },
          },
        },
        pageSizeOptions,
        paginationMode,
        sortingMode,
        hideFooter,
        disableRowSelectionOnClick: true,
        disableColumnMenu: true,
        autoHeight: false,
      }),
      [pageSize, page, pageSizeOptions, paginationMode, sortingMode, hideFooter],
    );

    const dataGridContent = (
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        rowCount={rowCount}
        {...stableProps}
        onPaginationModelChange={onPaginationModelChange}
        onSortModelChange={onSortModelChange}
        paginationModel={{
          page,
          pageSize,
        }}
        density="comfortable"
        localeText={{
          MuiTablePagination: {
            labelDisplayedRows: ({ from, to, count }) => (count === 0 ? "0 of 0" : `${from}–${to} of ${count}`),
          },
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
        sx={{
          height: typeof height === "number" ? `${height}px` : height,
          border: "none",
          "& .MuiDataGrid-main": {
            borderRadius: 0,
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            fontSize: "1rem", // Changed from 0.875rem (14px) to 1rem (16px) for mobile accessibility
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.paper,
            borderBottom: `2px solid ${theme.palette.divider}`,
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              fontWeight: 600,
              fontSize: "1rem",
              padding: "12px 16px",
              "&:focus": {
                outline: "none",
              },
              "&:focus-within": {
                outline: "none",
              },
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              color: theme.palette.text.primary,
            },
            "& .MuiDataGrid-sortIcon": {
              color: theme.palette.text.secondary,
            },
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(odd)": {
              backgroundColor: theme.palette.background.paper,
            },
            "&:nth-of-type(even)": {
              backgroundColor: theme.palette.background.default,
            },
            "&:hover": {
              backgroundColor: `${theme.palette.primary.main}15 !important`,
              cursor: "pointer",
            },
            "&.Mui-selected": {
              backgroundColor: `${theme.palette.primary.main}25 !important`,
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}35 !important`,
              },
            },
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: `2px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.secondary,
            minHeight: 56,
          },
          "& .MuiTablePagination-root": {
            color: theme.palette.text.secondary,
          },
          "& .MuiTablePagination-selectIcon": {
            color: theme.palette.text.secondary,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.background.paper,
          },
          "& .MuiDataGrid-overlay": {
            backgroundColor: theme.palette.background.paper,
          },
          "& .MuiLinearProgress-root": {
            backgroundColor: `${theme.palette.primary.main}20`,
            "& .MuiLinearProgress-bar": {
              backgroundColor: theme.palette.primary.main,
            },
          },
        }}
      />
    );

    if (title) {
      return (
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: "#1f2937",
              fontWeight: 600,
              fontSize: "1.125rem",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 20,
                backgroundColor: "primary.main",
                borderRadius: 1,
              }}
            />
            {title}
          </Typography>
          <Box sx={{ width: "100%" }}>{dataGridContent}</Box>
        </Paper>
      );
    }

    return <Box sx={{ width: "100%" }}>{dataGridContent}</Box>;
  },
);
